import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productAPI } from '../services/api';
import * as repo from '../sync/repo';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import { fmtDate } from '../utils/date';
import { unitLabel } from '../utils/unit';
import BackButton from '../components/BackButton';
import MoneyInput from '../components/MoneyInput';

export default function InventoryDetail() {
  const { id }       = useParams();
  const { business } = useAuth();
  const { toast }    = useToast();
  const navigate     = useNavigate();

  const [product,   setProduct]   = useState(null);
  const [sales,     setSales]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showRestock, setShowRestock] = useState(false);
  const [restockForm, setRestockForm] = useState({ qty: '', unit_cost: '', supplier: '' });
  const [showEdit,  setShowEdit]  = useState(false);
  const [editForm,  setEditForm]  = useState({});

  const load = async () => {
    try {
      const pRes = await productAPI.getById(id);
      const p = pRes.data.product;
      setProduct(p);
      setEditForm({
        name: p.name,
        sku: p.sku || '',
        unit: p.unit,
        sale_price: p.sale_price,
        reorder_level: p.reorder_level || '',
      });
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404) toast('Failed to load product', 'error');
      setLoading(false);
      return;
    }
    try {
      const sRes = await productAPI.getSales(id);
      setSales(sRes.data.sales || []);
    } catch {
      setSales([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const handleRestock = async (e) => {
    e.preventDefault();
    try {
      const result = await repo.createPurchase({
        product_id: id,
        qty: Number(restockForm.qty),
        unit_cost: Number(restockForm.unit_cost) || 0,
        supplier: restockForm.supplier || null,
      });
      toast(result._offline ? 'Restock queued (offline)' : 'Stock updated', 'success');
      setShowRestock(false);
      setRestockForm({ qty: '', unit_cost: '', supplier: '' });
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await repo.updateProduct(id, {
        ...editForm,
        sale_price: Number(editForm.sale_price),
        reorder_level: editForm.reorder_level ? Number(editForm.reorder_level) : null,
      });
      toast('Product updated', 'success');
      setShowEdit(false);
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleVoid = async () => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await repo.voidProduct(id);
      toast('Product deleted', 'success');
      navigate('/inventory');
    } catch { toast('Failed to delete product', 'error'); }
  };

  const fmt  = (n) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  const fmtQ = (n) => parseFloat(Number(n || 0).toFixed(3)).toString();

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;
  if (!product) return (
    <div className="page">
      <BackButton />
      <p className="text-muted" style={{ marginTop: '1rem' }}>Product not found.</p>
    </div>
  );

  return (
    <div className="page">
      {/* Header — click to edit */}
      <BackButton />
      <div
        className="card"
        onClick={() => setShowEdit(v => !v)}
        style={{ marginBottom: '1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{showEdit ? editForm.name || product.name : product.name}</div>
          {product.sku && <div className="text-muted" style={{ fontSize: '0.82rem' }}>SKU: {product.sku}</div>}
        </div>
        <span style={{ fontSize: '0.8rem', color: 'var(--color-primary)', flexShrink: 0, marginLeft: '0.75rem' }}>
          {showEdit ? '✕' : '›'}
        </span>
      </div>

      {/* Edit form */}
      {showEdit && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleEdit}>
            <div className="form-group"><label>Name</label><input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required autoFocus /></div>
            <div className="form-group"><label>SKU</label><input value={editForm.sku} onChange={e => setEditForm(f => ({ ...f, sku: e.target.value }))} /></div>
            <div className="form-group"><label>Unit</label><input value={editForm.unit} onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))} /></div>
            <div className="form-group"><label>Sale price ({business?.currency})</label><MoneyInput value={editForm.sale_price} onChange={e => setEditForm(f => ({ ...f, sale_price: e.target.value }))} required /></div>
            <div className="form-group"><label>Reorder level</label><input type="number" step="0.001" value={editForm.reorder_level} onChange={e => setEditForm(f => ({ ...f, reorder_level: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn-primary">Save</button>
              <button type="button" className="btn-ghost" onClick={() => setShowEdit(false)}>Cancel</button>
              <button type="button" className="btn-danger" style={{ marginLeft: 'auto' }} onClick={handleVoid}>Delete</button>
            </div>
          </form>
        </div>
      )}

      {/* Stock card */}
      <div className="card" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>{product.is_service ? 'Type' : 'In stock'}</div>
          {product.is_service ? (
            <div style={{ fontSize: '2rem', fontWeight: 700 }}>
              Service<span style={{ fontSize: '1rem', fontWeight: 400, color: 'var(--color-muted)' }}> · stock not tracked</span>
            </div>
          ) : (
            <div style={{
              fontSize: '2rem', fontWeight: 700,
              color: product.oversold ? 'var(--color-danger)' : Number(product.stock_qty) <= (Number(product.reorder_level) || 0) && product.reorder_level ? 'var(--color-warning)' : 'inherit',
            }}>
              {fmtQ(product.stock_qty)}{unitLabel(product.unit) && <span style={{ fontSize: '1rem', fontWeight: 400, color: 'var(--color-muted)' }}> {unitLabel(product.unit)}</span>}
            </div>
          )}
          {!product.is_service && product.oversold && <div style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>⚠ Oversold — reconcile stock</div>}
          {!product.is_service && !product.oversold && product.reorder_level && Number(product.stock_qty) <= Number(product.reorder_level) &&
            <div style={{ color: 'var(--color-warning)', fontSize: '0.8rem' }}>Low stock · Reorder level: {fmtQ(product.reorder_level)}</div>
          }
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!product.is_service && <button className="btn-primary" onClick={() => setShowRestock(!showRestock)}>+ Restock</button>}
          <Link to={`/sell?product=${id}`} className="btn-ghost" style={{ padding: '0.6rem 1rem', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 600 }}>Sell</Link>
        </div>
      </div>

      {/* Sale price */}
      <div
        className="card"
        onClick={() => setShowEdit(v => !v)}
        style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
      >
        <span className="text-muted">Sale price</span>
        <span style={{ fontWeight: 600 }}>{business?.currency} {fmt(product.sale_price)}{unitLabel(product.unit) ? ` / ${unitLabel(product.unit)}` : ''}</span>
      </div>

      {/* Restock form */}
      {showRestock && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.95rem', marginBottom: '0.75rem' }}>Record restock</h3>
          <form onSubmit={handleRestock}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Qty received *</label>
                <input type="number" step="0.001" min="0.001" value={restockForm.qty} onChange={e => setRestockForm(f => ({ ...f, qty: e.target.value }))} required autoFocus />
              </div>
              <div className="form-group">
                <label>Unit cost ({business?.currency})</label>
                <MoneyInput value={restockForm.unit_cost} onChange={e => setRestockForm(f => ({ ...f, unit_cost: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label>Supplier</label>
              <input value={restockForm.supplier} onChange={e => setRestockForm(f => ({ ...f, supplier: e.target.value }))} placeholder="Optional" />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn-primary">Confirm restock</button>
              <button type="button" className="btn-ghost" onClick={() => setShowRestock(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Sales history */}
      <h2 style={{ fontSize: '0.9rem', color: 'var(--color-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Sales history ({sales.length})
      </h2>

      {sales.length === 0 && <p className="text-muted">No sales recorded yet.</p>}

      {sales.map(s => (
        <div key={s.id} className="card" style={{ marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{s.buyer_name}</div>
              <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                {fmtQ(s.qty)}{unitLabel(product.unit) ? ` ${unitLabel(product.unit)}` : ''} × {business?.currency} {fmt(s.unit_price)} = {business?.currency} {fmt(s.total_amount)}
              </div>
              {s.due_date && s.payment_status !== 'paid' && (
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Due: {fmtDate(s.due_date)}</div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
              <span className={`badge badge-${s.payment_status}`}>{s.payment_status}</span>
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>{fmtDate(s.sold_at)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
